// ============================================================
// Client-portal API — public /v2/portal/* (resolve, login, accept, reset,
// me) plus the v2 report endpoints read with the portal session token.
// Its own reducerPath + baseQuery: never sends the dashboard JWT, and a
// 401 signs out only the portal session (never the agency user).
// Report shapes/mappers are shared with v2Api (./v2Mappers).
// ============================================================

import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { portalSignedOut, type PortalSessionState } from '@/modules/portal/portalSessionSlice'

import type { PortalMe, PortalPublic, PortalSessionResponse } from './portalTypes'
import { V2_BASE_URL } from './v2Api'
import {
    mapBreakdown,
    mapEventsReport,
    mapOverview,
    mapTimeseries,
    reportQuery,
    unwrap,
    type BreakdownResponse,
    type BreakdownRow,
    type EcommerceReport,
    type GoalsReport,
    type Overview,
    type PerformanceReport,
    type RealtimeReport,
    type RetentionReport,
    type SeriesPoint,
    type TimeseriesResponse,
    type V2Envelope,
    type V2RangeArgs,
} from './v2Mappers'

const rawBaseQuery = fetchBaseQuery({
    baseUrl: V2_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as { portalSession: PortalSessionState }).portalSession.token
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    },
})

/** Public calls whose 401 means "wrong credentials", not "session expired". */
const PUBLIC_PATHS = [
    '/portal/resolve',
    '/portal/login',
    '/portal/accept',
    '/portal/reset',
    '/portal/forgot',
]

export function isSessionCall(url: string): boolean {
    return !PUBLIC_PATHS.some(p => url.startsWith(p))
}

const portalBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    const result = await rawBaseQuery(args, api, extraOptions)
    const url = typeof args === 'string' ? args : args.url
    if (result.error?.status === 401 && isSessionCall(url)) {
        api.dispatch(portalSignedOut())
    }
    return result
}

const reports = (projectId: string) => `/projects/${encodeURIComponent(projectId)}/reports`

export type PortalLookup = { slug: string } | { host: string }

export const portalApi = createApi({
    reducerPath: 'portal',
    baseQuery: portalBaseQuery,
    endpoints: builder => ({
        // ---------- public ----------
        resolvePortal: builder.query<PortalPublic, PortalLookup>({
            query: by =>
                'slug' in by
                    ? `/portal/resolve?slug=${encodeURIComponent(by.slug)}`
                    : `/portal/resolve?host=${encodeURIComponent(by.host)}`,
            transformResponse: (res: V2Envelope<PortalPublic>) => unwrap(res),
        }),
        portalLogin: builder.mutation<
            PortalSessionResponse,
            { portal_id: string; email: string; password: string }
        >({
            query: body => ({ url: '/portal/login', method: 'POST', body }),
            transformResponse: (res: V2Envelope<PortalSessionResponse>) => unwrap(res),
        }),
        portalAccept: builder.mutation<
            PortalSessionResponse,
            { token: string; password: string; name?: string }
        >({
            query: body => ({ url: '/portal/accept', method: 'POST', body }),
            transformResponse: (res: V2Envelope<PortalSessionResponse>) => unwrap(res),
        }),
        portalForgot: builder.mutation<unknown, { portal_id: string; email: string }>({
            query: body => ({ url: '/portal/forgot', method: 'POST', body }),
        }),
        portalReset: builder.mutation<PortalSessionResponse, { token: string; password: string }>({
            query: body => ({ url: '/portal/reset', method: 'POST', body }),
            transformResponse: (res: V2Envelope<PortalSessionResponse>) => unwrap(res),
        }),
        portalMe: builder.query<PortalMe, void>({
            query: () => '/portal/me',
            transformResponse: (res: V2Envelope<PortalMe>) => unwrap(res),
        }),

        // ---------- reports (portal token; st = the client) ----------
        portalOverview: builder.query<Overview, V2RangeArgs>({
            query: a => `${reports(a.projectId)}/overview?${reportQuery(a)}`,
            transformResponse: mapOverview,
        }),
        portalTimeseries: builder.query<
            SeriesPoint[],
            V2RangeArgs & { metric: string; interval: 'hour' | 'day' | 'week' | 'month' }
        >({
            query: a =>
                `${reports(a.projectId)}/timeseries?${reportQuery(a, { metric: a.metric, interval: a.interval })}`,
            transformResponse: (res: V2Envelope<TimeseriesResponse>) => mapTimeseries(res),
        }),
        portalBreakdown: builder.query<
            BreakdownRow[],
            V2RangeArgs & { dim: string; event?: string; limit?: number }
        >({
            query: a =>
                `${reports(a.projectId)}/breakdown?${reportQuery(a, { dim: a.dim, event: a.event, limit: a.limit })}`,
            transformResponse: (res: V2Envelope<BreakdownResponse>) => mapBreakdown(res),
        }),
        portalEvents: builder.query<BreakdownRow[], V2RangeArgs>({
            query: a => `${reports(a.projectId)}/events?${reportQuery(a)}`,
            transformResponse: mapEventsReport,
        }),
        portalEcommerce: builder.query<EcommerceReport, V2RangeArgs & { limit?: number }>({
            query: a => `${reports(a.projectId)}/ecommerce?${reportQuery(a, { limit: a.limit })}`,
            transformResponse: (res: V2Envelope<EcommerceReport>) => unwrap(res),
        }),
        portalGoals: builder.query<GoalsReport, V2RangeArgs>({
            query: a => `${reports(a.projectId)}/goals?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<GoalsReport>) => unwrap(res),
        }),
        portalRetention: builder.query<
            RetentionReport,
            V2RangeArgs & { interval: 'day' | 'week'; periods: number }
        >({
            query: a =>
                `${reports(a.projectId)}/retention?${reportQuery(a, { interval: a.interval, periods: a.periods })}`,
            transformResponse: (res: V2Envelope<RetentionReport>) => unwrap(res),
        }),
        portalPerformance: builder.query<PerformanceReport, V2RangeArgs>({
            query: a => `${reports(a.projectId)}/performance?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<PerformanceReport>) => unwrap(res),
        }),
        portalRealtime: builder.query<
            RealtimeReport,
            { projectId: string; subTenant: string | null; minutes?: number }
        >({
            query: a => {
                const q = new URLSearchParams({ minutes: String(a.minutes ?? 30) })
                if (a.subTenant) {
                    q.set('st', a.subTenant)
                }
                return `${reports(a.projectId)}/realtime?${q.toString()}`
            },
            transformResponse: (res: V2Envelope<RealtimeReport>) => unwrap(res),
        }),
    }),
})

export const {
    useResolvePortalQuery,
    usePortalLoginMutation,
    usePortalAcceptMutation,
    usePortalForgotMutation,
    usePortalResetMutation,
    usePortalMeQuery,
    usePortalOverviewQuery,
    usePortalTimeseriesQuery,
    usePortalBreakdownQuery,
    usePortalEventsQuery,
    usePortalEcommerceQuery,
    usePortalGoalsQuery,
    usePortalRetentionQuery,
    usePortalPerformanceQuery,
    usePortalRealtimeQuery,
} = portalApi
